import { toInputString } from "@/lib/reference-data-cell";
import type { ReferenceDataColumn, ReferenceDataEditOperation } from "@/types/reference-data";

/** What a cell's editor offers when the column only accepts a closed set. */
export interface CellChoices {
  /** The accepted values, in the order the table declares them. */
  options: string[];
  /** The cell's current value when it is *not* one of `options` — kept
   *  selectable so switching to edit mode never silently rewrites it. */
  outsideDomain: string | null;
}

/** The closed set a cell may hold, or `null` when it is free text.
 *
 *  Two sources, both derived from the table itself (never a hardcoded list):
 *  the column's `allowed_values` (its CHECK constraint, e.g. `tier IN
 *  ('base','premium')`), and `boolean`, whose domain is implicit.
 *
 *  `currentValue` is the cell as the row holds it. A value outside the domain is
 *  possible — several CHECKs were added by later migrations — so it is reported
 *  rather than dropped: a bare `<select>` would coerce such a cell to its first
 *  option the moment it rendered, staging an edit the user never made.
 */
export function cellChoices(
  column: ReferenceDataColumn | undefined,
  currentValue: string,
): CellChoices | null {
  const options =
    column?.allowed_values && column.allowed_values.length > 0
      ? column.allowed_values
      : column?.data_type === "boolean"
        ? ["true", "false"]
        : null;
  if (!options) return null;
  return {
    options,
    outsideDomain: currentValue !== "" && !options.includes(currentValue) ? currentValue : null,
  };
}

/** A cell value coming from an `<input>` is always a string; an empty string
 *  means "not provided" — dropped so the backend treats a required column as
 *  missing (proper violation) and an optional one as its default. `null`/
 *  `undefined` are dropped too. Everything else (incl. JSON text for jsonb
 *  columns) is sent verbatim; the backend coerces / decodes it. */
function cleanValues(values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === "" || value === null || value === undefined) continue;
    out[key] = value;
  }
  return out;
}

/** Turn the RecordsTab staged state into the `/records/edit` operations payload.
 *
 *  - `edits`: existing-row id → the columns the user changed (changed only).
 *  - `newRows`: brand-new rows to create.
 *
 *  Operations whose cleaned `values` are empty are dropped, so no no-op edit is
 *  sent (an untouched staged row, or a new row left blank). Updates come before
 *  creates, but order does not matter to the backend.
 */
export function buildEditOperations(
  edits: Record<string, Record<string, unknown>>,
  newRows: Array<{ values: Record<string, unknown> }>,
): ReferenceDataEditOperation[] {
  const updates: ReferenceDataEditOperation[] = Object.entries(edits)
    .map(([id, values]) => ({ op: "update" as const, id, values: cleanValues(values) }))
    .filter((op) => Object.keys(op.values).length > 0);

  const creates: ReferenceDataEditOperation[] = newRows
    .map((row) => ({ op: "create" as const, values: cleanValues(row.values) }))
    .filter((op) => Object.keys(op.values).length > 0);

  return [...updates, ...creates];
}

/** Which control a column deserves in the row-edit dialog. */
export type FieldKind = "select" | "textarea" | "number" | "text";

/** Postgres types that hold free-form prose or a JSON document — the only ones
 *  that get a textarea. `character varying(n)` is short by design and stays a
 *  single-line input. */
const TEXTAREA_TYPES = new Set(["text", "json", "jsonb"]);

const NUMBER_TYPES = new Set([
  "smallint",
  "integer",
  "bigint",
  "numeric",
  "decimal",
  "real",
  "double precision",
]);

/** The control a column's editor should use.
 *
 *  A closed domain wins over the type: a `text` column with a CHECK is still a
 *  closed set, and offering free text there just invites a rejection. Choice of
 *  textarea is keyed on `data_type`, never on the current value's length —
 *  a length rule would swap the control while the user types and lose the caret.
 */
export function fieldKind(column: ReferenceDataColumn, currentValue: string): FieldKind {
  if (cellChoices(column, currentValue)) return "select";
  if (TEXTAREA_TYPES.has(column.data_type)) return "textarea";
  if (NUMBER_TYPES.has(column.data_type)) return "number";
  return "text";
}

/** The columns whose draft value differs from the row — what the dialog sends.
 *
 *  Compared through `toInputString` on both sides: every input yields a string
 *  while the row holds real types, so a raw compare would report `7` vs `"7"`
 *  (and a re-serialised jsonb object) as an edit and stage every column on every
 *  save. Columns absent from the draft are untouched by definition.
 */
export function changedCells(
  row: Record<string, unknown>,
  draft: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [column, value] of Object.entries(draft)) {
    if (value !== toInputString(row[column])) out[column] = value;
  }
  return out;
}

/** Todas as violações da recusa de uma edição, por linha ou em lote. */
export function recordViolations(err: unknown): string[] {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (!detail || typeof detail !== "object") return [];
  const violations = (detail as { violations?: unknown }).violations;
  return Array.isArray(violations) ? violations.map(String) : [];
}

/** Human message of an axios error, falling back when the API sent none. */
export function errMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback
  );
}
